import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Check,
  ChevronsUpDown,
  Loader2,
  X,
} from 'lucide-react';

const TOTAL_STEPS = 5;

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia',
  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
  'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

const DSOQuoteIntakePage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [selectedStates, setSelectedStates] = useState([]);
  const [statesPopoverOpen, setStatesPopoverOpen] = useState(false);
  const [formData, setFormData] = useState({
    legalEntityName: '',
    headquartersCity: '',
    headquartersState: '',
    numberOfStates: '',
    contactFirstName: '',
    contactLastName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    numberOfLocations: '',
    numberOfProviders: '',
    numberOfStaffLogins: '',
    numberOfAdminStaff: '',
    inboundCallsPerWeek: '',
    outboundCallsPerWeek: '',
    specialties: '',
    practiceManagementSystems: '',
    communicationSoftware: '',
    numberOfPhoneLines: '',
    numberOfPhoneExtensions: '',
    eraEnrollmentStatus: '',
    eftEnrollmentStatus: '',
    billingModel: '',
    insuranceClaimsPerMonth: '',
    numberOfTaxIds: '',
    numberOfGroupNpis: '',
    additionalNotes: '',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleState = (st) => {
    setSelectedStates((prev) =>
      prev.includes(st) ? prev.filter((s) => s !== st) : [...prev, st]
    );
  };

  const validateStep = (s) => {
    if (s === 1) {
      if (!formData.legalEntityName || !formData.headquartersCity || !formData.headquartersState) {
        return 'Please fill in the legal entity name and headquarters location.';
      }
      if (selectedStates.length === 0) {
        return 'Please select at least one state you operate in.';
      }
      if (!formData.numberOfStates) {
        return 'Please tell us how many states you operate in.';
      }
    }
    if (s === 2) {
      if (
        !formData.contactFirstName ||
        !formData.contactLastName ||
        !formData.contactTitle ||
        !formData.contactEmail ||
        !formData.contactPhone
      ) {
        return 'Please fill in all primary contact fields.';
      }
    }
    if (s === 3) {
      if (
        !formData.numberOfLocations ||
        !formData.numberOfProviders ||
        !formData.numberOfStaffLogins ||
        !formData.numberOfAdminStaff ||
        !formData.inboundCallsPerWeek ||
        !formData.outboundCallsPerWeek ||
        !formData.specialties
      ) {
        return 'Please fill in all operations fields.';
      }
    }
    if (s === 4) {
      if (
        !formData.practiceManagementSystems ||
        !formData.communicationSoftware ||
        !formData.numberOfPhoneLines ||
        !formData.numberOfPhoneExtensions
      ) {
        return 'Please list your existing PMS, communication software, and phone line / extension counts.';
      }
    }
    if (s === 5) {
      if (
        !formData.eraEnrollmentStatus ||
        !formData.eftEnrollmentStatus ||
        !formData.billingModel ||
        !formData.insuranceClaimsPerMonth ||
        !formData.numberOfTaxIds ||
        !formData.numberOfGroupNpis
      ) {
        return 'Please answer all billing and enrollment questions.';
      }
    }
    return '';
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError('');
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const handleBack = () => {
    setSubmitError('');
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateStep(step);
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1');
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        (isLocalhost ? 'http://localhost:8001' : '');

      const payload = {
        ...formData,
        statesOperated: selectedStates.join(', '),
      };

      const response = await fetch(`${apiUrl}/api/dso-quote-intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to submit intake');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting DSO quote intake:', error);
      setSubmitError(
        error.message || 'There was an error submitting your intake. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>

      <main className="pt-24 pb-12 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-2xl">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight text-center mb-3">
            DSO Quote Intake Form
          </h1>
          <p className="text-center text-slate-500 text-sm mb-6 max-w-lg mx-auto">
            A few questions about your organization so we can put together an accurate quote.
          </p>

          <div className="w-full relative z-10">
            <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
              <div className="h-1.5 bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 transition-all duration-300"
                  style={{ width: `${(isSubmitted ? 1 : step / TOTAL_STEPS) * 100}%` }}
                />
              </div>

              {isSubmitted ? (
                <CardContent className="px-6 py-10">
                  <div className="space-y-5 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-blue-100 bg-blue-50">
                      <CheckCircle2 className="h-7 w-7 text-blue-600" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-slate-900">
                        Thanks — we've got everything we need
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed max-w-md mx-auto">
                        Our team will review your intake and follow up at{' '}
                        <span className="font-semibold text-slate-900">{formData.contactEmail}</span>.
                        We'll either get back to you with a tailored quote or reach out with a few clarifying questions if we need more information.
                      </p>
                    </div>
                  </div>
                </CardContent>
              ) : (
                <>
                  <CardHeader className="pb-3 pt-6 px-6">
                    <CardTitle className="text-lg font-bold text-slate-900">
                      {step === 1 && 'Your organization'}
                      {step === 2 && 'Primary contact'}
                      {step === 3 && 'Operations & scale'}
                      {step === 4 && 'Current systems'}
                      {step === 5 && 'Billing & insurance'}
                    </CardTitle>
                    <CardDescription className="text-slate-500 text-sm mt-1">
                      {step === 1 && 'Legal entity, headquarters, and where you operate.'}
                      {step === 2 && "Who should we work with on this quote?"}
                      {step === 3 && 'Locations, providers, staff logins, and specialties.'}
                      {step === 4 && 'The PMS and communication tools you use today.'}
                      {step === 5 && 'A few questions about ERA/EFT enrollment and your billing mix.'}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-6 pb-6">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (step < TOTAL_STEPS) handleNext();
                        else handleSubmit(e);
                      }}
                    >
                      {/* STEP 1 — Organization */}
                      {step === 1 && (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="legalEntityName" className="text-xs font-semibold text-slate-700">Legal entity name *</Label>
                            <Input
                              id="legalEntityName"
                              placeholder="Bright Smile Dental Partners, LLC"
                              value={formData.legalEntityName}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5 col-span-2">
                              <Label htmlFor="headquartersCity" className="text-xs font-semibold text-slate-700">HQ city *</Label>
                              <Input
                                id="headquartersCity"
                                placeholder="Austin"
                                value={formData.headquartersCity}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="headquartersState" className="text-xs font-semibold text-slate-700">HQ state *</Label>
                              <Select
                                value={formData.headquartersState}
                                onValueChange={(value) => handleSelectChange('headquartersState', value)}
                              >
                                <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent className="max-h-64">
                                  {US_STATES.map((st) => (
                                    <SelectItem key={st} value={st}>{st}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">
                              States you operate in *
                            </Label>
                            <Popover open={statesPopoverOpen} onOpenChange={setStatesPopoverOpen}>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  role="combobox"
                                  aria-expanded={statesPopoverOpen}
                                  className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-slate-200 bg-slate-50 hover:bg-white focus:bg-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm text-left transition-all"
                                >
                                  <span className={selectedStates.length === 0 ? 'text-slate-400' : 'text-slate-700'}>
                                    {selectedStates.length === 0
                                      ? 'Search and select states...'
                                      : `${selectedStates.length} state${selectedStates.length === 1 ? '' : 's'} selected`}
                                  </span>
                                  <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="p-0 w-[--radix-popover-trigger-width] max-w-[--radix-popover-trigger-width]"
                                align="start"
                              >
                                <Command>
                                  <CommandInput placeholder="Search states..." className="h-9" />
                                  <CommandList>
                                    <CommandEmpty>No state found.</CommandEmpty>
                                    <CommandGroup>
                                      {US_STATES.map((st) => {
                                        const active = selectedStates.includes(st);
                                        return (
                                          <CommandItem
                                            key={st}
                                            value={st}
                                            onSelect={() => toggleState(st)}
                                            className="cursor-pointer"
                                          >
                                            <div
                                              className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                active
                                                  ? 'bg-blue-600 border-blue-600 text-white'
                                                  : 'border-slate-300 bg-white'
                                              }`}
                                            >
                                              {active && <Check className="h-3 w-3" />}
                                            </div>
                                            <span>{st}</span>
                                          </CommandItem>
                                        );
                                      })}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>

                            {selectedStates.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 pt-1">
                                {selectedStates.map((st) => (
                                  <span
                                    key={st}
                                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                  >
                                    {st}
                                    <button
                                      type="button"
                                      onClick={() => toggleState(st)}
                                      className="rounded-sm hover:bg-blue-100 p-0.5 transition-colors"
                                      aria-label={`Remove ${st}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="numberOfStates" className="text-xs font-semibold text-slate-700">
                              How many states do you operate in? *
                            </Label>
                            <Input
                              id="numberOfStates"
                              type="number"
                              min="1"
                              placeholder="e.g. 4"
                              value={formData.numberOfStates}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {/* STEP 2 — Contact */}
                      {step === 2 && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="contactFirstName" className="text-xs font-semibold text-slate-700">First name *</Label>
                              <Input
                                id="contactFirstName"
                                placeholder="Jane"
                                value={formData.contactFirstName}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="contactLastName" className="text-xs font-semibold text-slate-700">Last name *</Label>
                              <Input
                                id="contactLastName"
                                placeholder="Doe"
                                value={formData.contactLastName}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="contactTitle" className="text-xs font-semibold text-slate-700">Title / role *</Label>
                            <Input
                              id="contactTitle"
                              placeholder="VP of Operations"
                              value={formData.contactTitle}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="contactEmail" className="text-xs font-semibold text-slate-700">Work email *</Label>
                            <Input
                              id="contactEmail"
                              type="email"
                              placeholder="jane@brightsmile.com"
                              value={formData.contactEmail}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="contactPhone" className="text-xs font-semibold text-slate-700">Phone number *</Label>
                            <Input
                              id="contactPhone"
                              type="tel"
                              placeholder="(555) 000-0000"
                              value={formData.contactPhone}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {/* STEP 3 — Operations */}
                      {step === 3 && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="numberOfLocations" className="text-xs font-semibold text-slate-700">Number of locations *</Label>
                              <Input
                                id="numberOfLocations"
                                type="number"
                                min="1"
                                placeholder="e.g. 12"
                                value={formData.numberOfLocations}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="numberOfProviders" className="text-xs font-semibold text-slate-700">Total providers *</Label>
                              <Input
                                id="numberOfProviders"
                                type="number"
                                min="1"
                                placeholder="e.g. 38"
                                value={formData.numberOfProviders}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="numberOfStaffLogins" className="text-xs font-semibold text-slate-700">
                              Total staff logins across locations *
                            </Label>
                            <Input
                              id="numberOfStaffLogins"
                              type="number"
                              min="1"
                              placeholder="e.g. 145"
                              value={formData.numberOfStaffLogins}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                            <p className="text-[11px] text-slate-500">
                              Approximate count of unique staff users across all offices (front desk, billing, RCM, ops, etc).
                            </p>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="numberOfAdminStaff" className="text-xs font-semibold text-slate-700">
                              Total admin staff across your organization *
                            </Label>
                            <Input
                              id="numberOfAdminStaff"
                              type="number"
                              min="1"
                              placeholder="e.g. 60"
                              value={formData.numberOfAdminStaff}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                            <p className="text-[11px] text-slate-500">
                              Non-clinical staff — front desk, billing, RCM, operations, leadership.
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="inboundCallsPerWeek" className="text-xs font-semibold text-slate-700">
                                Inbound calls per week *
                              </Label>
                              <Input
                                id="inboundCallsPerWeek"
                                type="number"
                                min="0"
                                placeholder="e.g. 2,400"
                                value={formData.inboundCallsPerWeek}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="outboundCallsPerWeek" className="text-xs font-semibold text-slate-700">
                                Outbound calls per week *
                              </Label>
                              <Input
                                id="outboundCallsPerWeek"
                                type="number"
                                min="0"
                                placeholder="e.g. 1,800"
                                value={formData.outboundCallsPerWeek}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">
                            Best estimate of total inbound / outbound call volume across all locations.
                          </p>

                          <div className="space-y-1.5">
                            <Label htmlFor="specialties" className="text-xs font-semibold text-slate-700">
                              Specialties you operate in *
                            </Label>
                            <Textarea
                              id="specialties"
                              placeholder="e.g. General, Pediatrics, Ortho, Endo, Oral Surgery, Perio"
                              value={formData.specialties}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[64px] resize-none transition-all text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {/* STEP 4 — Current systems */}
                      {step === 4 && (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="practiceManagementSystems" className="text-xs font-semibold text-slate-700">
                              Existing practice management systems *
                            </Label>
                            <Textarea
                              id="practiceManagementSystems"
                              placeholder={'List all PMS in use across locations, with counts.\ne.g. Dentrix (8 locations), Open Dental (3 locations), Eaglesoft (1 location)'}
                              value={formData.practiceManagementSystems}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[100px] resize-none transition-all text-sm"
                            />
                            <p className="text-[11px] text-slate-500">
                              Include every PMS used across your portfolio and how many offices use each.
                            </p>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="communicationSoftware" className="text-xs font-semibold text-slate-700">
                              Existing communication software *
                            </Label>
                            <Textarea
                              id="communicationSoftware"
                              placeholder={'e.g. Weave for VoIP + texting at 9 locations, Mango Voice at 3 locations'}
                              value={formData.communicationSoftware}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[80px] resize-none transition-all text-sm"
                            />
                            <p className="text-[11px] text-slate-500">
                              Voice / web phones / patient messaging — whatever you use today (Weave, Mango, RingCentral, etc).
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="numberOfPhoneLines" className="text-xs font-semibold text-slate-700">
                                Phone lines across locations *
                              </Label>
                              <Input
                                id="numberOfPhoneLines"
                                type="number"
                                min="1"
                                placeholder="e.g. 32"
                                value={formData.numberOfPhoneLines}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="numberOfPhoneExtensions" className="text-xs font-semibold text-slate-700">
                                Phone extensions across locations *
                              </Label>
                              <Input
                                id="numberOfPhoneExtensions"
                                type="number"
                                min="1"
                                placeholder="e.g. 120"
                                value={formData.numberOfPhoneExtensions}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">
                            Approximate totals — voice lines / DIDs and individual extensions in use across every office.
                          </p>
                        </div>
                      )}

                      {/* STEP 5 — Billing & insurance + free form */}
                      {step === 5 && (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="eraEnrollmentStatus" className="text-xs font-semibold text-slate-700">
                              ERA enrollment across offices *
                            </Label>
                            <Select
                              value={formData.eraEnrollmentStatus}
                              onValueChange={(value) => handleSelectChange('eraEnrollmentStatus', value)}
                            >
                              <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mostly-enrolled">Mostly ERA-enrolled</SelectItem>
                                <SelectItem value="some-enrolled">Some enrolled, some paper EOBs</SelectItem>
                                <SelectItem value="mostly-paper">Mostly paper EOBs</SelectItem>
                                <SelectItem value="unsure">Not sure</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="eftEnrollmentStatus" className="text-xs font-semibold text-slate-700">
                              EFT enrollment across offices *
                            </Label>
                            <Select
                              value={formData.eftEnrollmentStatus}
                              onValueChange={(value) => handleSelectChange('eftEnrollmentStatus', value)}
                            >
                              <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mostly-enrolled">Mostly EFT-enrolled</SelectItem>
                                <SelectItem value="some-enrolled">Some enrolled, some paper checks</SelectItem>
                                <SelectItem value="mostly-paper">Mostly paper checks</SelectItem>
                                <SelectItem value="unsure">Not sure</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="billingModel" className="text-xs font-semibold text-slate-700">
                              Billing model across offices *
                            </Label>
                            <Select
                              value={formData.billingModel}
                              onValueChange={(value) => handleSelectChange('billingModel', value)}
                            >
                              <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mostly-in-network">Mostly in-network / billing insurance</SelectItem>
                                <SelectItem value="mixed">Mixed in-network and out-of-network</SelectItem>
                                <SelectItem value="mostly-out-of-network">Mostly out-of-network / fee-for-service</SelectItem>
                                <SelectItem value="medicaid-heavy">Heavy Medicaid presence</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="insuranceClaimsPerMonth" className="text-xs font-semibold text-slate-700">
                              Insurance claims submitted per month *
                            </Label>
                            <Input
                              id="insuranceClaimsPerMonth"
                              type="number"
                              min="0"
                              placeholder="e.g. 4,500"
                              value={formData.insuranceClaimsPerMonth}
                              onChange={handleInputChange}
                              required
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                            />
                            <p className="text-[11px] text-slate-500">
                              Average monthly claim volume across all entities and locations.
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="numberOfTaxIds" className="text-xs font-semibold text-slate-700">
                                Number of tax IDs you bill under *
                              </Label>
                              <Input
                                id="numberOfTaxIds"
                                type="number"
                                min="1"
                                placeholder="e.g. 3"
                                value={formData.numberOfTaxIds}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="numberOfGroupNpis" className="text-xs font-semibold text-slate-700">
                                Number of group NPIs you have *
                              </Label>
                              <Input
                                id="numberOfGroupNpis"
                                type="number"
                                min="1"
                                placeholder="e.g. 5"
                                value={formData.numberOfGroupNpis}
                                onChange={handleInputChange}
                                required
                                className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="additionalNotes" className="text-xs font-semibold text-slate-700">
                              Anything else we should know? <span className="text-slate-400 font-normal">(Optional)</span>
                            </Label>
                            <Textarea
                              id="additionalNotes"
                              placeholder="Upcoming acquisitions, RCM pain points, timing, anything that would help us tailor the quote..."
                              value={formData.additionalNotes}
                              onChange={handleInputChange}
                              className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[100px] resize-none transition-all text-sm"
                            />
                          </div>

                          <div className="rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-xs text-slate-600 leading-relaxed">
                            <span className="font-semibold text-slate-900">What happens next:</span>{' '}
                            Once you submit, we'll either get back to you with a quote, or reach out with a few clarifying questions if we need more information to put one together.
                          </div>
                        </div>
                      )}

                      {submitError && (
                        <p className="text-sm text-red-600 pt-3" role="alert">
                          {submitError}
                        </p>
                      )}

                      {/* Navigation */}
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleBack}
                          disabled={step === 1 || isSubmitting}
                          className="text-slate-600 border-slate-200 hover:bg-slate-50 h-10 px-5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ArrowLeft className="mr-2 w-4 h-4" />
                          Back
                        </Button>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6 text-sm font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                              Submitting...
                            </>
                          ) : step < TOTAL_STEPS ? (
                            <>
                              Continue
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Submit intake
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </div>

                      <p className="text-[10px] text-center text-slate-400 mt-4">
                        Step {step} of {TOTAL_STEPS}
                      </p>
                    </form>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DSOQuoteIntakePage;
